import type { FailCodeConcernEntity } from '../entities';

import { StsBackendOperation } from '../../sts-backend-operation';

/**
 * Parameters for fetching fail code concerns. Both a location code and frequency code are required
 * because concerns are the third level in the NTF cascade and depend on the prior two selections.
 */
export type FetchConcernsParams = {
    /** The selected frequency code to fetch concerns for. */
    frequencyCode: string;

    /** The selected location code that the frequency belongs to. */
    locationCode: string;

    /** Optional abort signal to cancel the request. */
    signal?: AbortSignal;
};

/**
 * Fetch fail code concerns for a selected location and frequency. Concerns
 * are the third-level selection in the no-trouble-found (NTF) cascade used
 * by both QMRs and Techline cases.
 */
export class FetchConcerns extends StsBackendOperation {
    /**
     * Fetch concerns from the API and return them as domain entities.
     *
     * @param params - the location code, frequency code, and optional abort signal
     * @returns the concern entities
     */
    async exec(params: FetchConcernsParams): Promise<FailCodeConcernEntity[]> {
        const { signal, ...request } = params;

        // Fetch concerns from the backend
        const response = await this.client.fetchFailCodeConcerns(request, signal);

        if (!response.success) {
            return [];
        }

        return response.data.concerns ?? [];
    }
}
